import React from "react";

import {
    createRoutesFromChildren,
    matchRoutes,
    useLocation,
    useNavigationType,
} from "@remix-run/react";
import * as Sentry from "@sentry/react";

import { getSentryDsn, isSentryEnabled } from "~/lib/env.shared";
import {
    scrubSentryBreadcrumb,
    scrubSentryEvent,
} from "~/lib/logger-redaction";

export const setupSentry = () => {
    if (!isSentryEnabled()) return;

    Sentry.init({
        dsn: getSentryDsn(),
        integrations: [
            Sentry.reactRouterV6BrowserTracingIntegration({
                useEffect: React.useEffect,
                useLocation,
                useNavigationType,
                createRoutesFromChildren,
                matchRoutes,
            }),
        ],
        // Tracing
        tracesSampleRate: 1.0, //  Capture 100% of the transactions
        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: [
            "localhost",
            /^https:\/\/app\.ifscompanion\.com\/api/,
            /^https:\/\/app\.ifscompanion\.org\/api/,
            /^https:\/\/app\.ifsobserver\.com\/api/,
        ],
        tracesSampler: (samplingContext) => {
            if (samplingContext.name.includes("http-intake")) {
                return 0;
            }
            if (samplingContext.name.includes("http-intake")) {
                return 0;
            }
            return 1;
        },
        // AIDEV-NOTE: HIPAA — never send PII to Sentry; strip identifiers + request bodies.
        sendDefaultPii: false,
        beforeSend: (event) => {
            scrubSentryEvent(event);
            if (event.user) {
                delete event.user.email;
                delete event.user.username;
                delete event.user.ip_address;
            }
            if (event.request) {
                delete event.request.data;
                delete event.request.cookies;
                delete event.request.headers;
            }
            return event;
        },
        beforeBreadcrumb: (breadcrumb) => scrubSentryBreadcrumb(breadcrumb),
    });
};

// AIDEV-NOTE: HIPAA — identify Sentry users by opaque id ONLY. Email is a direct
// identifier (PHI in this product) and must not be sent to Sentry.
export const setSentryUser = (userId: string | null) => {
    if (!isSentryEnabled()) return;

    Sentry.setUser({
        id: userId || undefined,
    });
};
